import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Check, X, Sun, Wrench, Zap } from "lucide-react";

export default function PackageCompare() {
  const [kind, setKind] = useState("solar");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/packages/${kind}`)
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Load failed"))
      .finally(() => setLoading(false));
  }, [kind]);

  const rowKeys = kind === "solar"
    ? [
        ["price", "Price", (v) => `$${(v || 0).toLocaleString()}`],
        ["system_size_kw", "System size", (v) => v ? `${v} kW` : "—"],
        ["panels", "Panels", (v) => v || "—"],
        ["monthly_savings", "Est. monthly savings", (v) => v ? `$${v}` : "—"],
        ["monthly_from", "Financing from", (v) => v ? `$${v}/mo` : "—"],
        ["co2_offset_tons", "CO₂ offset", (v) => v ? `${v} t/yr` : "—"],
        ["warranty_years", "Warranty", (v) => v ? `${v} years` : "—"],
        ["install_days", "Install time", (v) => v ? `${v} days` : "—"],
      ]
    : [
        ["price", "Annual price", (v) => `$${(v || 0).toLocaleString()}`],
        ["visits_per_year", "Site visits", (v) => v ? `${v} per year` : "—"],
        ["response_hours", "Response SLA", (v) => v ? `${v} hours` : "—"],
      ];

  return (
    <div data-testid="package-compare" className="max-w-6xl mx-auto">
      <p className="overline">Compare</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-6">
        Which package fits you best?
      </h1>

      <div className="flex gap-2 mb-6">
        {[["solar", Sun, "Solar"], ["maintenance", Wrench, "Maintenance"]].map(([k, Icon, lbl]) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${
              kind === k ? "bg-[#0A2540] text-white" : "bg-slate-100 dark:bg-slate-800 text-[#475569]"
            }`}
          >
            <Icon className="w-4 h-4" /> {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#94A3B8] italic py-12 text-center">No packages available.</p>
      ) : (
        <>
          {/* Card row */}
          <div className={`grid gap-4 mb-8`} style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}>
            {rows.map((p) => (
              <div key={p.package_id} data-testid={`pkg-${p.package_id}`}
                   className={`card-pb p-5 relative ${p.popular ? "border-2 border-[#C9A356]" : ""}`}>
                {p.popular && (
                  <span className="absolute -top-2 right-4 text-[9px] uppercase tracking-wider font-bold bg-[#C9A356] text-white px-2 py-0.5 rounded-full">
                    Most popular
                  </span>
                )}
                <div className="text-3xl mb-2">{p.icon || "☀"}</div>
                <div className="font-heading text-xl text-[#0A2540] dark:text-white">{p.name}</div>
                {p.tagline && <div className="text-xs text-[#475569] dark:text-slate-400 mt-1">{p.tagline}</div>}
                <div className="mt-4">
                  <div className="text-3xl font-bold font-heading text-[#0A2540] dark:text-white">${(p.price || 0).toLocaleString()}</div>
                  {p.monthly_from && <div className="text-xs text-[#475569]">or ${p.monthly_from}/mo financed</div>}
                </div>
                {p.features && p.features.length > 0 && (
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {p.features.slice(0, 5).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                        <span className="text-[#475569] dark:text-slate-400">{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Spec comparison table */}
          <div className="card-pb overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400">Spec</th>
                  {rows.map((p) => (
                    <th key={p.package_id} className="px-4 py-3 text-left text-xs uppercase tracking-wider text-[#0A2540] dark:text-white">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowKeys.map(([key, label, fmt]) => (
                  <tr key={key} className="border-b border-[#E2E8F0] dark:border-slate-700" data-testid={`cmp-row-${key}`}>
                    <td className="px-4 py-2.5 text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 font-medium">{label}</td>
                    {rows.map((p) => (
                      <td key={p.package_id} className="px-4 py-2.5 text-sm text-[#0A2540] dark:text-white">{fmt(p[key])}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-4 py-3"></td>
                  {rows.map((p) => (
                    <td key={p.package_id} className="px-4 py-3">
                      <a href={`/dashboard/booking?package_id=${p.package_id}`}
                         className="text-xs text-[#C9A356] hover:text-[#8A6B14] inline-flex items-center gap-1 font-medium">
                        <Zap className="w-3 h-3" /> Get this package
                      </a>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
